import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Quotation } from 'data-layer-cr/dist/models';
import {
  cardTypes,
  CheckBox,
  colors,
  DateField,
  DefaultCard,
  FormHookResult,
  FormValueTypes,
  HeaderText,
  MessageBox,
  NetworkIndicator,
  RadioButtonGroup,
  RadioDisplayItem,
  TextField,
} from 'design-system';
import React, { useState } from 'react';

import {
  filterFractionsByPaymentType,
  findFractionByQuantity,
  getFractionsGridSize,
} from 'src/functions/findFractionByQuantity';
import { getQuantityTypeFromFractionation } from 'src/functions/fractionationMethods';
import { Fraction } from 'src/models/helperTypes/FractionationType';

import ConsentCollectionModal from 'src/components/screens/EmissionPolicy/PaymentData/components/ConsentCollectionModal';

import {
  makeCreditCardField,
  makeExpitarionCardField,
  makeTextField,
} from 'src/components/MakeFormFields';

import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';

import {
  bankTransferPayment,
  creditCard,
  sinpePayment,
} from 'src/constants/paymentTypes';

import profiles from 'src/constants/profileTypes';
import PaymentOptionCard from './components/PaymentOptionCard';

import {
  BankTransferInstructions,
  creditCardInstructions,
  disclaimerHasCardTypeAmex,
  disclaimerHasFreeMonth,
  disclaimerHasMonthlyPayment,
  discleimerAmexAnnualMessage,
  discleimerAnnualMessage,
  SinpePaymentInstructions,
} from './constantViewMethod';
import { getPaymentDayNumber } from './dataMethods';

const useStyles = makeStyles({
  customText: {
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: '22px',
    color: '#4A4B4E',
  },
  linkText: {
    fontWeight: 700,
    fontSize: '15px',
    lineHeight: '22px',
    color: colors.bright_turquoise,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  gridMargin: {
    marginTop: '10px',
  },
  linkSmall: {
    color: colors.bright_turquoise,
    background: 'transparent',
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 20,
    paddingLeft: 20,
    borderRadius: 80,
    '&:hover': {
      background: 'transparent',
    },
    '&:active': {
      background: 'transparent',
    },
  },
});

type FormFieldProps = {
  inputName: string;
  label: string;
  getInputProps: FormHookResult;
  inputId: string;
  helperText?: string;
  isDisabled?: boolean;
  showError?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  shouldUpdate?: (newVal: string) => boolean;
  defaultValue?: boolean;
  style?: React.CSSProperties;
};

const generateAlternativePaymentOptions = (
  moneyType: string | undefined,
  paymentType: string,
): RadioDisplayItem[] => {
  const radioOptions: RadioDisplayItem[] = [
    {
      Value: bankTransferPayment,
      Text: 'Transferencia bancaria (aplica recargo por método de pago)',
      decorator: (
        <BankTransferInstructions
          paymentType={paymentType}
          moneyType={moneyType}
        />
      ),
    },
  ];
  if (moneyType === '₡') {
    radioOptions.push({
      Value: sinpePayment,
      Text: 'SINPE Móvil (aplica recargo por método de pago)',
      decorator: <SinpePaymentInstructions paymentType={paymentType} />,
    });
  }
  return radioOptions;
};

type FormQuotationProps = {
  readonly paymentType: string;
  readonly moneyType: string | undefined;
  readonly onChangePayment: (newValue: string) => void;
  readonly showOtherPaymentMethods: boolean;
  readonly setShowOtherPaymentsMethods: (newVal: boolean) => void;
  readonly hideOtherOptions?: boolean;
  readonly profileType?: string;
};

function CustomLinkExpand({
  paymentType,
  moneyType,
  onChangePayment,
  showOtherPaymentMethods,
  setShowOtherPaymentsMethods,
  hideOtherOptions,
  profileType,
}: FormQuotationProps): React.ReactNode {
  const classes = useStyles();
  function onShowOtherPaymentMethods(): void {
    setShowOtherPaymentsMethods(!showOtherPaymentMethods);
  }
  const radioOptions = generateAlternativePaymentOptions(
    moneyType,
    paymentType,
  );
  return (
    <>
      {/* title */}
      <Grid item xs={12}>
        <Typography variant="body2">Realizarás tu pago mediante:</Typography>
      </Grid>
      {/* instructions */}
      <Grid item xs={12}>
        <RadioButtonGroup
          formTitle=""
          radioGroupName="default-paymentType"
          gridSize={12}
          radioData={[
            {
              Value: 'TCR',
              Text: 'Tarjeta de crédito o débito',
              decorator: <div>{creditCardInstructions}</div>,
            },
          ]}
          defaultValue={paymentType}
          onChange={(val): void => onChangePayment(val.Value)}
        />
      </Grid>
      {!hideOtherOptions && profileType !== profiles.RETENCION && (
        <Grid item container justifyContent="center" xs={12}>
          <Button
            data-testid="show-payment-methods-options"
            id="sales-web-inspection-button-show-payment-methods"
            endIcon={
              showOtherPaymentMethods ? (
                <ExpandLessIcon style={{ fontSize: 25 }} />
              ) : (
                <ExpandMoreIcon style={{ fontSize: 25 }} />
              )
            }
            onClick={onShowOtherPaymentMethods}
            className={classes.linkSmall}
          >
            Otros métodos de pago
          </Button>
        </Grid>
      )}
      {!hideOtherOptions &&
        showOtherPaymentMethods &&
        profileType !== profiles.RETENCION && (
          <Grid item xs={12}>
            <RadioButtonGroup
              formTitle=""
              radioGroupName="extra-paymentType"
              gridSize={12}
              radioData={radioOptions}
              defaultValue={paymentType}
              onChange={(val): void => onChangePayment(val.Value)}
            />
          </Grid>
        )}
    </>
  );
}

export function chooseDisclaimerMonthMessage(
  hasFreeMonth: boolean,
  isAmexCardType: boolean,
): string {
  if (hasFreeMonth) {
    return disclaimerHasFreeMonth;
  }

  if (isAmexCardType) {
    return disclaimerHasCardTypeAmex;
  }

  return disclaimerHasMonthlyPayment;
}

type PaymentQuantityCardProps = {
  getInputProps: FormHookResult;
  quotation: Quotation;
  fractionations: Fraction[];
  isLoading: boolean;
  setPaymentFraction: (value: number) => void;
};

export function updatePaymentData(
  getInputProps: FormHookResult,
  fraction: Fraction,
): void {
  getInputProps('paymentQuantityType').onChange(
    getQuantityTypeFromFractionation(fraction),
  );
  getInputProps('paymentQuantity').onChange(fraction.installmentNumber);
  getInputProps('paymentAmount').onChange(fraction.installmentAmount);
}

/**
 * Extracted to prevent sonarcloud from complaining
 */
export function makePaymentQuantityCard({
  getInputProps,
  quotation,
  fractionations,
  isLoading,
  setPaymentFraction,
}: PaymentQuantityCardProps): React.ReactNode {
  const paymentQuantity = getInputProps('paymentQuantity').value as number;
  const paymentType = getInputProps('type').value as string;
  const profileType = quotation.quotationProfile.type;

  /** remove trimestral and monthly fractionation optios for payment types
   *  different to credit card
   */
  const finalFractions = filterFractionsByPaymentType(
    fractionations,
    paymentType,
    profileType,
  );
  const fractionGridSize = getFractionsGridSize(paymentType, profileType);

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      justifyContent="center"
      style={{ minWidth: 275, margin: 0 }}
    >
      <NetworkIndicator
        data-testid="network-indicator-payment-options"
        requesting={isLoading}
        message="Cargando franccionamientos"
      />
      {finalFractions.map((fraction) => (
        <Grid
          item
          xs={6}
          lg={fractionGridSize}
          container
          key={fraction.installmentNumber}
        >
          <PaymentOptionCard
            data-testid={`payment-option-card-${fraction.installmentNumber}`}
            fractionation={fraction}
            isSelected={paymentQuantity === fraction.installmentNumber}
            typeOfMoney={quotation.quotationProfile.typeOfMoney}
            onClickFraction={(): void => {
              setPaymentFraction(fraction.installmentNumber);
              updatePaymentData(getInputProps, fraction);
            }}
            profileType={profileType}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export function chooseDisclaimerAnnualMessage(isAmexCardType: boolean): string {
  return isAmexCardType ? discleimerAmexAnnualMessage : discleimerAnnualMessage;
}

export function makePolicyStartDateField(
  getInputProps: FormHookResult,
  today: Date,
  twoMonthsAhead: Date,
  producId: string,
  paymentFrequency: number | null,
  paymentType: string,
): React.ReactElement {
  const policyStartDate = getInputProps('policyStartDate');
  const paymentDayNumber = getInputProps('paymentDayNumber');
  return (
    <DefaultCard
      data-testid="pay-day-card"
      title="¿Cuándo deseas que inicie la vigencia de la póliza?"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DateField
            inputId="sales-web-inspection-input-policystartdate"
            inputName="policyStartDate"
            inputLabel="Fecha de inicio vigencia"
            minDate={today}
            maxDate={twoMonthsAhead}
            defaultValue={policyStartDate.value as Date}
            onChange={(value: string) => {
              const paymentDayNumberValue = getPaymentDayNumber(
                value.split('/')[0],
              );
              policyStartDate.onChange(value);
              paymentDayNumber.onChange(paymentDayNumberValue);
            }}
            fallbackValue={undefined}
          />
        </Grid>
        {paymentType === creditCard && (
          <Grid item xs={12} sm={6}>
            <TextField
              inputId="sales-web-inspection-input-paymentdaynumber"
              inputName="paymentDayNumber"
              inputLabel="Día para los siguientes cobros"
              defaultValue={paymentDayNumber.value as string}
              isDisabled
            />
          </Grid>
        )}
        <Grid item xs={12}>
          {['RetencionBacUSD', 'RetencionBacCRC'].includes(producId) ? (
            <MessageBox
              hideCloseButton
              variant="info"
              message={
                paymentFrequency === 1
                  ? 'Al ser el primer mes gratuito, se descontará 1 cuota del primer pago anual.'
                  : 'Al ser el primer mes gratuito, el cobro de la primera cuota se hará efectivo al inicio del segundo mes.'
              }
            />
          ) : (
            <MessageBox
              hideCloseButton
              variant="info"
              message={`${
                paymentType === creditCard
                  ? 'El cobro de la primera cuota se hará efectivo el día de hoy.'
                  : 'Te estaremos contactando con antelación, para informarte cuando se debe realizar el próximo pago.'
              }`}
            />
          )}
        </Grid>
      </Grid>
    </DefaultCard>
  );
}

export const shouldUpdateSecurityCode = (
  newValue: string,
  isAmex: boolean,
): boolean => {
  const securityMaxLength = isAmex ? 4 : 3;
  return (
    /[a-zA-Z]+/.exec(newValue) === null && newValue.length <= securityMaxLength
  );
};

type CustomCheckboxProps = FormFieldProps & {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  showCardInfo: boolean;
};

function CustomCheckbox({
  inputName,
  getInputProps,
  openModal,
  setOpenModal,
  showCardInfo,
}: CustomCheckboxProps): React.ReactNode {
  const classesStyle = useStyles();

  function handleEffectSubmit(): void {
    setOpenModal(false);
    getInputProps(inputName).onChange(true);
  }

  if (!showCardInfo) {
    return <></>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box>
          <CheckBox
            data-testid={inputName}
            inputId={`swi-checkbox-${inputName}`}
            checkName={getInputProps(inputName).name}
            defaultValue={getInputProps(inputName).value as boolean}
            onChange={getInputProps(inputName).onChange}
            style={{
              alignItems: 'center',
              fontStyle: '22px !important',
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1, marginLeft: '-20px' }}>
          <Typography className={classesStyle.customText}>
            Autorizo a ASSA Compañía de Seguros S.A. a realizar el rebajo de los
            montos pendientes, así como los ajustes correspondientes a la misma,
            por medio del siguiente cargo automático a la tarjeta.{' '}
            <Box
              data-testid="swi-collection-read-more"
              component="span"
              onClick={(): void => {
                setOpenModal(true);
              }}
              className={classesStyle.linkText}
            >
              Leer más
            </Box>
          </Typography>
        </Box>
      </Box>
      <ConsentCollectionModal
        data-testid="swi-collection-consent-modal"
        open={openModal}
        onClose={(): void => {
          setOpenModal(false);
        }}
        onAccept={handleEffectSubmit}
        onCheck={getInputProps(inputName).onChange}
        value={getInputProps(inputName).value as boolean}
      />
    </>
  );
}

type CreditCardInputType = {
  getInputProps: FormHookResult;
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  showCardFields: boolean;
};

export function CreditCardInput({
  getInputProps,
  openModal,
  setOpenModal,
  showCardFields,
}: CreditCardInputType): React.ReactNode {
  const { value: cardType } = getInputProps('cardType');

  return (
    <>
      {showCardFields && (
        <>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <HeaderText gutterBottom>Datos del tarjetahabiente</HeaderText>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {makeTextField({
                      inputId: 'swi-credit-input-cardName',
                      inputName: 'cardName',
                      label: 'Nombre en la tarjeta',
                      getInputProps,
                    })}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {makeCreditCardField({
                      inputId: 'swi-credit-input-cardNumber',
                      inputName: 'cardNumber',
                      cardTypeInputName: 'cardType',
                      label: 'Número de la tarjeta',
                      getInputProps,
                    })}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {makeExpitarionCardField({
                      inputId: 'swi-credit-input-expirationDate',
                      inputName: 'expirationDate',
                      label: 'Fecha de expiración',
                      getInputProps,
                    })}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {makeTextField({
                      inputId: 'swi-credit-input-securityCode',
                      inputName: 'securityCode',
                      label: 'Código de seguridad',
                      shouldUpdate: (newVal) =>
                        shouldUpdateSecurityCode(
                          newVal,
                          cardType === cardTypes.AMEX,
                        ),
                      getInputProps,
                    })}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '10px' }}>
            <HeaderText gutterBottom>
              Autorización de cobro automático a mi tarjeta
            </HeaderText>
          </Grid>
        </>
      )}

      <Grid item xs={12} container alignItems="center">
        {CustomCheckbox({
          inputName: 'collectionAuthorization',
          label: '',
          inputId: 'sales-web-collection-authorization',
          getInputProps,
          openModal,
          setOpenModal,
          showCardInfo: showCardFields,
        })}
      </Grid>
    </>
  );
}

type PaymentMethodFormType = {
  readonly getInputProps: FormHookResult;
  readonly updateMultipleInputs: (newData: {
    [key: string]: FormValueTypes;
  }) => void;
  readonly moneyType: string | undefined;
  readonly fractionations: Fraction[];
  readonly hideOtherOptions?: boolean;
  readonly profileType?: string;
};

export function getDefaultPaymentQuantity(paymentType: string): number {
  return paymentType === creditCard ? 12 : 1;
}

export function PaymentMethodForm({
  getInputProps,
  moneyType,
  updateMultipleInputs,
  fractionations,
  hideOtherOptions,
  profileType,
}: PaymentMethodFormType): React.ReactNode {
  const [openModal, setOpenModal] = useState(false);
  const [showOtherPaymentMethods, setShowOtherPaymentsMethods] =
    useState(false);

  const paymentType = getInputProps('type').value as string;
  const onChangePayment = (newValue: string): void => {
    const paymentQuantity = getDefaultPaymentQuantity(newValue);
    const fraction = findFractionByQuantity(
      fractionations,
      paymentQuantity,
      newValue,
    );
    updateMultipleInputs({
      type: newValue,
      cardName: '',
      cardNumber: '',
      cardType: '',
      expirationDate: '',
      securityCode: '',
      paymentQuantityType: getQuantityTypeFromFractionation(fraction),
      paymentQuantity: fraction.installmentNumber,
      paymentAmount: fraction.installmentAmount,
    });
  };
  return (
    <DefaultCard data-testid="credit-card-form">
      <Grid container spacing={2}>
        {CustomLinkExpand({
          onChangePayment,
          moneyType,
          paymentType,
          showOtherPaymentMethods,
          setShowOtherPaymentsMethods,
          hideOtherOptions,
          profileType,
        })}

        {/* input fields */}
        {CreditCardInput({
          getInputProps,
          openModal,
          setOpenModal,
          showCardFields: paymentType === creditCard,
        })}
      </Grid>
    </DefaultCard>
  );
}
